import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import React, { useContext, lazy } from 'react';
import classnames from 'classnames';

import { FormattedMessage, useIntl } from 'react-intl';
import { Route, Routes, Link, useNavigate } from 'react-router-dom-v5-compat';
import { Button } from 'ui-components/components/button';

import SettingsPageWrapper from '../settings-page-wrapper';
import { pages } from '../nav-items';
import { IntegrationsContext, IntegrationsContextProvider } from './context';
import messages from './messages';
import style from './style.module.less';
import AmazonLogo from './svgs/amazon_logo_placeholder.svg';
import ConstantContactLogo from './svgs/v2/constant-contact-logo.svg';
import EdeskLogo from './svgs/v2/edesk-logo.svg';
import GoogleLogo from './svgs/v2/google-logo.svg';
import GorgiasLogo from './svgs/v2/gorgias-logo.svg';
import MailChimpLogo from './svgs/v2/mailchimp-color.svg';
import QuickbooksLogo from './svgs/v2/quickbooks-logo.svg';
import ShipperHQLogo from './svgs/v2/shipperHQ-logo.svg';
import WebhooksLogo from './svgs/webhooks-logo.svg';
import ZapierLogo from './svgs/v2/zapier-logo.svg';
import ReturnRabbitLogo from './svgs/v2/returnrabbit-logo.svg';
import { AMAZON_NAME, SANTOS_APP_ID, DISPLAY_ORDER } from './constants';
import { linkIDs } from '../../../services/link-service/links';
import HelpArticleLink from '../../../components/help-article-link';

const ConstantContact = lazy(() => import(/* webpackPrefetch: true */ './constant-contact').then((m) => ({ default: m.ConstantContact })));
const ReturnRabbit = lazy(() =>
  import(/* webpackPrefetch: true */ './returnrabbit/returnrabbit').then((m) => ({ default: m.ReturnRabbit })),
);
const Zapier = lazy(() => import(/* webpackPrefetch: true */ './zapier').then((m) => ({ default: m.Zapier })));
const Edesk = lazy(() => import(/* webpackPrefetch: true */ './e-desk').then((m) => ({ default: m.Edesk })));
const Gorgias = lazy(() => import(/* webpackPrefetch: true */ './gorgias').then((m) => ({ default: m.Gorgias })));
const Webhooks = lazy(() => import(/* webpackPrefetch: true */ './webhooks/index').then((m) => ({ default: m.WebHooks })));
const ShipperHQ = lazy(() => import(/* webpackPrefetch: true */ './shipper-hq/index').then((m) => ({ default: m.ShipperHQPage })));
const Google = lazy(() => import(/* webpackPrefetch: true */ './google/index').then((m) => ({ default: m.GooglePage })));
const Amazon = lazy(() => import(/* webpackPrefetch: true */ './amazon/index').then((m) => ({ default: m.AmazonPage })));
const QuickBooks = lazy(() => import(/* webpackPrefetch: true */ './quickbooks/index').then((m) => ({ default: m.Quickbooks })));
const MailChimp = lazy(() => import(/* webpackPrefetch: true */ './mailchimp/index').then((m) => ({ default: m.MailChimp })));

const Integrations = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { integrations } = useContext(IntegrationsContext);

  if (!integrations) {
    return null;
  }

  const svgLogoRender = (name, logoUri) => {
    const svgMap = {
      MailChimp: <img alt={intl.formatMessage(messages.mailChimpLogo)} src={logoUri || MailChimpLogo} />,
      QuickbooksOnline: <img alt={intl.formatMessage(messages.quickBooksLogo)} src={logoUri || QuickbooksLogo} />,
      Zapier: <img alt={intl.formatMessage(messages.zapierLogo)} src={logoUri || ZapierLogo} />,
      ReturnRabbit: <img alt={intl.formatMessage(messages.returnRabbitLogo)} src={logoUri || ReturnRabbitLogo} />,
      ConstantContact: <img alt={intl.formatMessage(messages.constantContactLogo)} src={logoUri || ConstantContactLogo} />,
      Edesk: <img alt={intl.formatMessage(messages.edeskLogo)} src={logoUri || EdeskLogo} />,
      Gorgias: <img alt={intl.formatMessage(messages.gorgiasLogo)} src={logoUri || GorgiasLogo} />,
      Webhooks: <img alt={intl.formatMessage(messages.webhooksLogo)} src={logoUri || WebhooksLogo} />,
      ShipperHQ: <img alt={intl.formatMessage(messages.shipperHQLogo)} src={logoUri || ShipperHQLogo} />,
      Google: <img alt={intl.formatMessage(messages.googleLogo)} src={logoUri || GoogleLogo} />,
      Amazon: <img alt={intl.formatMessage(messages.santosTestLogo)} src={logoUri || AmazonLogo} />,
    };

    return svgMap[name];
  };

  const sortIntegrationsList = (intgs) => {
    const result = [];

    DISPLAY_ORDER.forEach((next) => {
      const nextIntg = intgs.find((x) => x.name === next);

      if (nextIntg !== undefined) {
        result.push(nextIntg);
      }
    });

    return result;
  };

  const formatIntegrationName = (name) => {
    if (name === 'MailChimp') {
      return 'Mailchimp';
    }

    if (name === 'Edesk') {
      return 'eDesk';
    }

    return name;
  };

  const renderSVGState = (count, name, logoUri) => {
    if (count > 0) {
      return (
        <>
          <div className={classnames(style.logo, style['active-logo'])}>{svgLogoRender(name, logoUri)}</div>
        </>
      );
    }
    return <div className={style.logo}>{svgLogoRender(name, logoUri)}</div>;
  };

  const renderBlurb = (name) => {
    const blurbMap = {
      MailChimp: <p className={style['integration-blurb']}>{intl.formatMessage(messages.mailChimpBlurb)}</p>,

      QuickbooksOnline: <p className={style['integration-blurb']}>{intl.formatMessage(messages.quickBooksBlurb)}</p>,

      Zapier: <p className={style['integration-blurb']}>{intl.formatMessage(messages.zapierBlurb)}</p>,

      ReturnRabbit: <p className={style['integration-blurb']}>{intl.formatMessage(messages.returnRabbitBlurb)}</p>,

      ConstantContact: <p className={style['integration-blurb']}>{intl.formatMessage(messages.constantContactBlurb)}</p>,

      Edesk: <p className={style['integration-blurb']}>{intl.formatMessage(messages.edeskBlurb)}</p>,

      Gorgias: <p className={style['integration-blurb']}>{intl.formatMessage(messages.gorgiasBlurb)}</p>,

      Webhooks: <p className={style['integration-blurb']}>{intl.formatMessage(messages.webhooksBlurb)}</p>,

      ShipperHQ: <p className={style['integration-blurb']}>{intl.formatMessage(messages.shipperHQBlurb)}</p>,

      Google: <p className={style['integration-blurb']}>{intl.formatMessage(messages.googleBlurb)}</p>,

      Amazon: <p className={style['integration-blurb']}>{intl.formatMessage(messages.amazonBlurb)}</p>,
    };

    return blurbMap[name];
  };

  const renderFeaturedSection = (name) => {
    const featuredIntegrations = ['Google'];

    if (featuredIntegrations.includes(name)) {
      return <input className={style['featured-tag']} readOnly value=' Featured ' />;
    }

    return <div className={style['integrations-item-section']} />;
  };

  const renderActiveIndicator = (intgCount) => {
    if (intgCount > 0) {
      return <FontAwesomeIcon className={style.checkmark} icon={faCheck} />;
    }

    return <div className={style.inactive} />;
  };

  const renderConnectionControl = (intgCount, name) => {
    if (intgCount > 0) {
      return <Link to={{ pathname: `${name}` }}>Manage Settings</Link>;
    }
    // TODO: this page should be intl'd
    return (
      <Button onClick={() => navigate(name)} theme='secondary'>
        Connect
      </Button>
    );
  };

  const gridHeader = (
    <div className={style['grid-header']}>
      <div className={style['header-item']}>
        <FormattedMessage {...messages.integration} />
      </div>
      <div className={style['header-item']} />
      <div className={classnames(style['header-item'], style['active-header'])}>
        <FormattedMessage {...messages.statusIndicator} />
      </div>
      <div className={style['header-item']}>
        <FormattedMessage {...messages.actionsHeader} />
      </div>
    </div>
  );

  return (
    <SettingsPageWrapper
      messages={{
        headerMessage: messages.header,
      }}
    >
      <div className={style['integrations-settings']}>
        <div className={style['integrations-subText']}>
          <FormattedMessage
            {...messages.subText}
            values={{
              link: (chunks) => <HelpArticleLink articleId={linkIDs.CommunityIdeas}>{chunks}</HelpArticleLink>,
            }}
          />
        </div>
      </div>
      <div className={style['grid-container']}>
        {gridHeader}
        {sortIntegrationsList(integrations).map((integration, idx) => {
          const name = integration.integratedAppID === SANTOS_APP_ID ? AMAZON_NAME : integration.name.replace(/\s/g, '');
          return (
            <div key={idx} className={style['integrations-list-item']}>
              <div className={style['integrations-item-section']}>
                {renderSVGState(integration.count, name, integration.logoUri)}
                <div className={style['integration-blurb-container']}>
                  <p className={style['integration-blurb-name']}>{formatIntegrationName(integration.name)}</p>
                  {renderBlurb(name)}
                </div>
              </div>
              <div className={style['integrations-item-section']}>{renderFeaturedSection(name)}</div>
              <div className={style['integrations-item-section']}>{renderActiveIndicator(integration.count)}</div>
              <div className={style['integrations-item-section']}>{renderConnectionControl(integration.count, name)}</div>
            </div>
          );
        })}
      </div>
    </SettingsPageWrapper>
  );
};

export const IntegrationsRouter = () => {
  return (
    <IntegrationsContextProvider>
      <Routes>
        <Route element={<Integrations />} path={`/${pages.integrations.location}`} />
        <Route element={<Zapier />} path={`/${pages.integrations.location}/${pages.zapier.location}`} />
        <Route element={<ReturnRabbit />} path={`/${pages.integrations.location}/${pages.returnRabbit.location}`} />
        <Route element={<Edesk />} path={`/${pages.integrations.location}/${pages.eDesk.location}`} />
        <Route element={<Gorgias />} path={`/${pages.integrations.location}/${pages.gorgias.location}`} />
        <Route element={<Webhooks />} path={`/${pages.integrations.location}/${pages.webhooks.location}`} />
        <Route element={<ShipperHQ />} path={`/${pages.integrations.location}/${pages.shipperHQ.location}`} />
        <Route element={<Google />} path={`/${pages.integrations.location}/${pages.google.location}`} />
        <Route element={<Amazon />} path={`/${pages.integrations.location}/${pages.amazon.location}`} />
        <Route element={<QuickBooks />} path={`/${pages.integrations.location}/${pages.quickbooksOnline.location}`} />
        <Route element={<MailChimp />} path={`/${pages.integrations.location}/${pages.mailChimp.location}`} />
        <Route element={<ConstantContact />} path={`/${pages.integrations.location}/${pages.constantContact.location}`} />
      </Routes>
    </IntegrationsContextProvider>
  );
};
